// Typography shared by the TinyMCE editing surface and the read-only lesson view.
//
// TinyMCE renders the editable body inside an iframe, so the app's stylesheets do
// not reach it — the rules have to be handed over as a `content_style` string.
// Keeping the single source here means "what you type" and "what is published"
// stay the same document, which is what makes a round trip to Word survive.
//
// The values come from the Training Manual's own reference document
// (Claritick 4/34/3376, "Representation Agreement.docx"): Calibri 12pt, single
// spacing with no space after a paragraph, and half-inch list indents. Matching
// them exactly is what lets a section be pasted either direction unchanged.
//
// Carlito is metric-compatible with Calibri and covers Linux/CI, where Calibri
// is not installed; the generic sans-serif is the last resort.

export const CONTENT_FONT_STACK = `Calibri, Carlito, 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif`;
export const CONTENT_FONT_SIZE = '12pt';
// w:line="240" w:lineRule="exact" with w:after="0" — Word single spacing.
export const CONTENT_LINE_HEIGHT = '1.15';

// Word restarts an ordered list whenever it meets a new list element, so the
// numbering it produces on paste only survives if the nesting is expressed the
// way HTML requires (a sub-list inside its parent <li>). `listStyles` states the
// per-level markers explicitly rather than relying on browser defaults, which
// differ for levels 3+ and were rendering Roman numerals as decimals.
const listStyles = `
  ol { list-style-type: decimal; }
  ol ol { list-style-type: lower-alpha; }
  ol ol ol { list-style-type: lower-roman; }
  ol ol ol ol { list-style-type: decimal; }
  ol ol ol ol ol { list-style-type: lower-alpha; }
  ol ol ol ol ol ol { list-style-type: lower-roman; }
  ul { list-style-type: disc; }
  ul ul { list-style-type: circle; }
  ul ul ul { list-style-type: square; }
  ul ul ul ul { list-style-type: disc; }
`;

export const editorContentStyle = `
  body {
    font-family: ${CONTENT_FONT_STACK};
    font-size: ${CONTENT_FONT_SIZE};
    line-height: ${CONTENT_LINE_HEIGHT};
    color: #21242c;
    margin: 24px 28px;
  }

  body .float-right { float: right; margin: 0 0 16px 24px; max-width: 50%; }
  body .float-left { float: left; margin: 0 24px 16px 0; max-width: 50%; }

  h1, h2, h3, h4, h5, h6, p, ol, ul, li, a, th, td, blockquote, dl, dt, dd {
    font-family: ${CONTENT_FONT_STACK};
  }

  h1 { font-size: 20pt; }
  h2 { font-size: 16pt; }
  h3 { font-size: 14pt; }
  h4, h5, h6 { font-size: 12pt; }
  h1, h2, h3, h4, h5, h6 { font-weight: 700; line-height: 1.3; margin: 1.2em 0 0.5em; }

  /* The reference sets w:after="0" on every paragraph: list items sit directly
     under one another with no gap. Standalone paragraphs keep one blank line so
     prose stays readable. */
  p { margin: 0 0 1em; }

  /* 0.5in per level, split between margin (the step) and padding (the marker's
     hanging indent) — with list-style-position:outside the marker is drawn in
     the padding, so padding alone collapses the levels together. */
  ol, ul { margin: 0; padding-left: 0.25in; }
  li { margin: 0 0 0.2em; }
  li > p { margin: 0 0 0.2em; }
  li > ol, li > ul { margin-left: 0.25in; }
  ${listStyles}

  table { border-collapse: collapse; }
  table th, table td { border: 1px solid #c8cdd7; padding: 6px 9px; }
`;

export default editorContentStyle;
